/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `b2bEventVirtualLineSchedules` for the Caintra Connect frontend.
 * @module utils/b2bEventVirtualLineSchedules
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
/**
 * Helpers for B2B virtual line flows: buyer schedule rows from participating companies
 * and optional time-slot filtering for virtual line appointments.
 */

import { canAccessB2BVirtualLineHub } from "./accessControl.js";

/** `HH:mm` prefix length, including the colon. */
const HHMM_LENGTH = 5;
/** Index of the colon in an `HH:mm` string. */
const HHMM_COLON_INDEX = 2;
/** `YYYY-MM-DD` prefix length. */
const YYYYMMDD_LENGTH = 10;
/** Index of the first dash in `YYYY-MM-DD`. */
const YYYYMMDD_YEAR_DASH_INDEX = 4;
/** Index of the second dash in `YYYY-MM-DD`. */
const YYYYMMDD_MONTH_DASH_INDEX = 7;
const MINUTES_PER_HOUR = 60;
const DECIMAL_RADIX = 10;

/** @param {Record<string, unknown>|null|undefined} user @param {unknown} [permissions] */
export const canAccessB2BEventUsersList = (user, permissions) =>
  canAccessB2BVirtualLineHub(user, permissions);

/** @param {Record<string, unknown>|null|undefined} user */
export const isExecutiveBuyerUser = (user) =>
  String(user?.tipoDeUsuario) === "11";

/**
 * Whether to show the executive-buyer schedule / virtual-line entry on a B2B event card.
 * Requires `usuarioEsEjecutivoComprador` from GET /b2bEvent/user/{userId}/management.
 *
 * @param {Record<string, unknown>|null|undefined} user
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {boolean}
 */
export const showBuyerOwnSchedulesButtonForEvent = (user, event) =>
  isExecutiveBuyerUser(user) && event?.usuarioEsEjecutivoComprador === true;

/**
 * @param {string|undefined|null} value
 * @returns {string}
 */
const toHHmm = (value) => {
  if (value == null) {
    return "";
  }
  const s = String(value).trim();
  if (s.length >= HHMM_LENGTH && s[HHMM_COLON_INDEX] === ":") {
    return s.slice(0, HHMM_LENGTH);
  }
  return s;
};

/**
 * @param {string|undefined|null} value
 * @returns {string}
 */
const toYyyyMmDd = (value) => {
  if (value == null) {
    return "";
  }
  const s = String(value).trim();
  if (
    s.length >= YYYYMMDD_LENGTH &&
    s[YYYYMMDD_YEAR_DASH_INDEX] === "-" &&
    s[YYYYMMDD_MONTH_DASH_INDEX] === "-"
  ) {
    return s.slice(0, YYYYMMDD_LENGTH);
  }
  return s;
};

/**
 * Blank and whitespace-only ids mean "every buyer".
 * A non-blank id is kept as written, including surrounding spaces.
 * @param {unknown} onlyBuyerUserId
 * @returns {string|null}
 */
const normalizeBuyerFilterId = (onlyBuyerUserId) => {
  if (onlyBuyerUserId == null || String(onlyBuyerUserId).trim() === "") {
    return null;
  }
  return String(onlyBuyerUserId);
};

/**
 * @param {unknown} value
 * @returns {string}
 */
const textOrEmpty = (value) => (value != null ? String(value) : "");

/**
 * @param {unknown} completos
 * @returns {Map<string, Record<string, unknown>>}
 */
const buyerDirectoryById = (completos) => {
  const users = Array.isArray(completos) ? completos : [];
  return new Map(users.map((user) => [String(user._id), user]));
};

/**
 * Schedule entries without an ejecutivo id are skipped.
 * @param {Record<string, unknown>|null|undefined} entry
 * @returns {string}
 */
const scheduleBuyerUserId = (entry) =>
  entry?._id != null ? String(entry._id) : "";

/**
 * Prefer the directory name, then either email field, then the user id.
 * @param {Record<string, unknown>|undefined} user
 * @param {string} buyerUserId
 * @returns {string}
 */
const buyerDisplayName = (user, buyerUserId) =>
  String(user?.nombre || user?.correoElectronico || user?.correo || buyerUserId);

/**
 * The index is the position in the company's schedule list, including skipped rows.
 * @param {Record<string, unknown>} company
 * @param {string} buyerUserId
 * @param {Record<string, unknown>} entry
 * @param {number} idx
 * @returns {string}
 */
const scheduleRowKey = (company, buyerUserId, entry, idx) =>
  `${company.idEmpresaCompradora ?? ""}-${buyerUserId}-${entry.fecha ?? ""}-${entry.horaInicio ?? ""}-${idx}`;

/**
 * @param {Record<string, unknown>} company
 * @param {Record<string, unknown>} entry
 * @param {string} buyerUserId
 * @param {string} buyerName
 * @param {number} idx
 * @returns {Object}
 */
const mapScheduleToRow = (company, entry, buyerUserId, buyerName, idx) => ({
  key: scheduleRowKey(company, buyerUserId, entry, idx),
  buyerUserId,
  buyerName,
  fecha: textOrEmpty(entry.fecha),
  horaInicio: textOrEmpty(entry.horaInicio),
  horaFin: textOrEmpty(entry.horaFin),
  idEmpresaCompradora: textOrEmpty(company.idEmpresaCompradora),
  nombreComercial: textOrEmpty(company.nombreComercial),
});

/**
 * One schedule entry, or null when it has no id or does not match the filter.
 * @param {Record<string, unknown>} company
 * @param {Map<string, Record<string, unknown>>} byId
 * @param {string|null} filterId
 * @param {Record<string, unknown>} entry
 * @param {number} idx
 * @returns {Object|null}
 */
const rowForScheduleEntry = (company, byId, filterId, entry, idx) => {
  const buyerUserId = scheduleBuyerUserId(entry);
  if (!buyerUserId) {
    return null;
  }
  if (filterId && buyerUserId !== filterId) {
    return null;
  }

  const user = byId.get(buyerUserId);
  return mapScheduleToRow(
    company,
    entry,
    buyerUserId,
    buyerDisplayName(user, buyerUserId),
    idx,
  );
};

/**
 * @param {Record<string, unknown>} company
 * @param {string|null} filterId
 * @returns {Object[]}
 */
const rowsForCompany = (company, filterId) => {
  const schedules = company.ejecutivosCompradores ?? [];
  const byId = buyerDirectoryById(company.ejecutivosCompradoresCompletos ?? []);
  const rows = [];

  schedules.forEach((entry, idx) => {
    const row = rowForScheduleEntry(company, byId, filterId, entry, idx);
    if (row) {
      rows.push(row);
    }
  });

  return rows;
};

/**
 * Buyer schedule rows from participating companies.
 * When `onlyBuyerUserId` is set, only that ejecutivo's rows are returned.
 * @param {unknown} companies - Participating company payloads
 * @param {string} [onlyBuyerUserId] - When set, only schedule rows for this ejecutivo `_id`
 * @returns {Array<{
 *   key: string,
 *   buyerUserId: string,
 *   buyerName: string,
 *   fecha: string,
 *   horaInicio: string,
 *   horaFin: string,
 *   idEmpresaCompradora: string,
 *   nombreComercial: string
 * }>}
 */
export const buildBuyerScheduleRowsFromParticipatingCompanies = (
  companies,
  onlyBuyerUserId,
) => {
  if (!Array.isArray(companies)) {
    return [];
  }

  const filterId = normalizeBuyerFilterId(onlyBuyerUserId);
  return companies.flatMap((company) => rowsForCompany(company, filterId));
};

/**
 * @param {string} hhmm
 * @returns {number|null}
 */
const toMinutes = (hhmm) => {
  if (!hhmm) {
    return null;
  }
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, DECIMAL_RADIX) || 0);
  return h * MINUTES_PER_HOUR + m;
};

/**
 * Half-open interval overlap (mirrors backend `timeRangesOverlap`).
 *
 * @param {number} startA
 * @param {number} endA
 * @param {number} startB
 * @param {number} endB
 */
const rangesOverlap = (startA, endA, startB, endB) =>
  startA < endB && startB < endA;

/**
 * Whether a virtual line appointment belongs to the selected navigation preset.
 * Matches `fecha` and buyer availability window from `ejecutivosCompradores`
 * (`horaInicio` / `horaFin`), using overlap semantics aligned with the backend
 * slot filter.
 *
 * @param {Record<string, unknown>} appointment
 * @param {{ fecha?: string, horaInicio?: string, horaFin?: string }|null|undefined} preset
 */
export const appointmentMatchesVirtualLineSlot = (appointment, preset) => {
  if (!preset?.fecha) {
    return true;
  }
  const aptDate = toYyyyMmDd(appointment?.fecha);
  const slotDate = toYyyyMmDd(preset.fecha);
  if (!aptDate || !slotDate || aptDate !== slotDate) {
    return false;
  }

  const aptStart = toHHmm(appointment?.horaInicio);
  const aptEnd = toHHmm(appointment?.horaFin);
  if (!aptStart || !aptEnd) {
    return false;
  }

  const windowStart = toHHmm(preset.horaInicio);
  const windowEnd = toHHmm(preset.horaFin);

  if (!windowStart && !windowEnd) {
    return true;
  }

  const a0 = toMinutes(aptStart);
  const a1 = toMinutes(aptEnd);
  const w0 = windowStart ? toMinutes(windowStart) : null;
  const w1 = windowEnd ? toMinutes(windowEnd) : null;

  if (w0 != null && w1 != null) {
    return rangesOverlap(a0, a1, w0, w1);
  }
  if (w0 != null) {
    return a0 >= w0;
  }
  return a1 <= w1;
};

/**
 * Query params for collaborator `GET …/appointments` slot narrowing.
 * Sends `fecha` and optional buyer availability window bounds from schedule presets.
 *
 * @param {Record<string, unknown>|null|undefined} preset
 * @returns {Record<string, string>|null}
 */
export const virtualLineSlotQueryParams = (preset) => {
  if (!preset?.fecha) {
    return null;
  }
  const fecha = toYyyyMmDd(preset.fecha);
  if (!fecha) {
    return null;
  }
  const horaInicio = toHHmm(preset.horaInicio);
  const horaFin = toHHmm(preset.horaFin);
  return {
    fecha,
    ...(horaInicio ? { horaInicio } : {}),
    ...(horaFin ? { horaFin } : {}),
  };
};
